import { useState, useMemo, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Save, Loader2 } from 'lucide-react';
import {
  useOrderLogicRule,
  useCreateOrderLogicRule,
  useUpdateOrderLogicRule,
} from '../../hooks/useOrderLogicRules';
import { useLenders } from '../../hooks/useLenders';
import { useProducts } from '../../hooks/useProducts';
import PageHeader from '../../components/common/PageHeader';
import RuleLevelEditor from '../../components/rules/RuleEditor';
import MultiSelectDropdown from '../../components/common/MultiSelectDropdown';
import type { RuleTrigger, RuleLevel } from '../../api/orderLogicRules';

const TRIGGERS: RuleTrigger[] = [
  'on_status_change',
  'on_order_creation',
  'on_order_completion',
  'on_order_failure',
];

export default function OrderLogicRuleEditPage() {
  const { urn } = useParams<{ urn: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const isNew = !urn;

  // Support pre-filling form data from router state (e.g. from the simulator page)
  const prefill = (location.state as { prefill?: Record<string, unknown> })?.prefill;

  const { data: existingRule, isLoading: loadingRule } = useOrderLogicRule(urn || '');
  const createRule = useCreateOrderLogicRule();
  const updateRule = useUpdateOrderLogicRule();
  const { data: lendersList, isLoading: loadingLenders } = useLenders();
  const { data: productsList, isLoading: loadingProducts } = useProducts();

  // Form state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [trigger, setTrigger] = useState<RuleTrigger>('on_order_creation');
  const [priority, setPriority] = useState(1);
  const [enabled, setEnabled] = useState(true);
  const [selectedLenders, setSelectedLenders] = useState<string[]>([]);
  const [selectedProducts, setSelectedProducts] = useState<string[]>([]);
  const [rootLevel, setRootLevel] = useState<RuleLevel>({ operator: 'AND', conditions: [], actions: [] });
  const [initialized, setInitialized] = useState(false);

  // Reset initialized when urn changes (e.g. navigating between rules)
  useEffect(() => {
    setInitialized(false);
  }, [urn]);

  // Populate form when editing an existing rule
  useEffect(() => {
    if (existingRule && !initialized) {
      setName(existingRule.name || '');
      setDescription(existingRule.description || '');
      setTrigger(existingRule.trigger || 'on_order_creation');
      setPriority(existingRule.priority || 1);
      setEnabled(existingRule.enabled ?? true);
      setSelectedLenders(existingRule.applicableLenders || []);
      setSelectedProducts(existingRule.applicableProducts || []);
      setRootLevel(existingRule.rootLevel || { operator: 'AND', conditions: [], actions: [] });
      setInitialized(true);
    }
  }, [existingRule, initialized]);

  // Populate form from prefill data (e.g. from simulator or AI-generated rule)
  useEffect(() => {
    if (isNew && prefill && !initialized) {
      const p = prefill as Record<string, unknown>;
      if (p.name) setName(p.name as string);
      if (p.description) setDescription(p.description as string);
      if (p.trigger) setTrigger(p.trigger as RuleTrigger);
      if (p.priority) setPriority(p.priority as number);
      if (typeof p.enabled === 'boolean') setEnabled(p.enabled);
      if (Array.isArray(p.applicableLenders)) setSelectedLenders(p.applicableLenders as string[]);
      if (Array.isArray(p.applicableProducts)) setSelectedProducts(p.applicableProducts as string[]);
      if (p.rootLevel) setRootLevel(p.rootLevel as RuleLevel);
      setInitialized(true);
    }
  }, [isNew, prefill, initialized]);

  const lenderOptions = useMemo(
    () =>
      (lendersList || []).map((l) => ({
        value: l.urn,
        label: l.displayName || l.name,
        sublabel: l.urn,
      })),
    [lendersList],
  );

  const productOptions = useMemo(
    () =>
      (productsList || []).map((p) => ({
        value: p.urn,
        label: p.productName,
        sublabel: p.urn,
      })),
    [productsList],
  );

  const isPending = createRule.isPending || updateRule.isPending;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const data = {
      name,
      description: description || undefined,
      trigger,
      priority,
      enabled,
      applicableLenders: selectedLenders,
      applicableProducts: selectedProducts,
      rootLevel,
    };

    if (isNew) {
      createRule.mutate(data, {
        onSuccess: () => navigate('/rules/order-logic'),
      });
    } else {
      updateRule.mutate(
        { urn: urn!, data },
        { onSuccess: () => navigate('/rules/order-logic') },
      );
    }
  };

  if (!isNew && loadingRule) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 text-blue-500 animate-spin" />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title={isNew ? 'New Order Logic Rule' : `Edit Rule: ${existingRule?.name || ''}`}
        description={isNew ? 'Create a new rule for order lifecycle automation' : 'Modify the rule configuration'}
        actions={
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => navigate('/rules/order-logic')}
              className="btn-secondary gap-1"
            >
              <ArrowLeft className="h-4 w-4" />
              Back
            </button>
            <button
              type="submit"
              form="rule-form"
              disabled={isPending || !name}
              className="btn-primary gap-1"
            >
              {isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              {isPending ? 'Saving...' : 'Save Rule'}
            </button>
          </div>
        }
      />

      <form id="rule-form" onSubmit={handleSubmit} className="space-y-4 lg:space-y-6">
        {/* Basic Info */}
        <div className="card p-4 lg:p-6">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-4">Basic Information</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4">
            <div className="sm:col-span-2">
              <label className="label">Name *</label>
              <input
                type="text"
                className="input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Create Title Order on Mortgage Completion"
                required
              />
            </div>
            <div>
              <label className="label">Trigger *</label>
              <select className="input" value={trigger} onChange={(e) => setTrigger(e.target.value as RuleTrigger)}>
                {TRIGGERS.map((t) => (
                  <option key={t} value={t}>
                    {t.replace(/^on_/, '').replace(/_/g, ' ')}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="label">Priority</label>
              <input
                type="number"
                className="input"
                value={priority}
                onChange={(e) => setPriority(Number(e.target.value))}
                min={0}
              />
              <p className="text-[11px] text-gray-400 mt-1">Higher = evaluated first</p>
            </div>
            <div className="sm:col-span-2 lg:col-span-4">
              <label className="label">Description</label>
              <input
                type="text"
                className="input"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Optional description of what this rule does"
              />
            </div>
            <div className="flex items-center gap-2 sm:col-span-2 lg:col-span-4">
              <input
                type="checkbox"
                id="rule-enabled"
                className="rounded"
                checked={enabled}
                onChange={(e) => setEnabled(e.target.checked)}
              />
              <label htmlFor="rule-enabled" className="text-sm text-gray-700 dark:text-gray-200">
                Enabled
              </label>
              <span className="text-xs text-gray-400 ml-1">
                {enabled ? 'This rule will be evaluated' : 'This rule is disabled and will be skipped'}
              </span>
            </div>
          </div>
        </div>

        {/* Scope */}
        <div className="card p-4 lg:p-6">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-4">Scope</h3>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
            Define which lenders and products this rule applies to. Leave empty to apply to all.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <MultiSelectDropdown
              label="Applicable Lenders"
              options={lenderOptions}
              selected={selectedLenders}
              onChange={setSelectedLenders}
              placeholder="All lenders (none selected)"
              isLoading={loadingLenders}
            />
            <MultiSelectDropdown
              label="Applicable Products"
              options={productOptions}
              selected={selectedProducts}
              onChange={setSelectedProducts}
              placeholder="All products (none selected)"
              isLoading={loadingProducts}
            />
          </div>
        </div>

        {/* Condition Tree */}
        <div className="card p-4 lg:p-6">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">Rule Logic</h3>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
            Define conditions that must be met and the actions to execute when they are.
          </p>
          <RuleLevelEditor level={rootLevel} onChange={setRootLevel} />
        </div>
      </form>
    </div>
  );
}
